import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Radio, Users, Shuffle, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import PublicSidePill, { readPublicSide } from "@/components/PublicSidePill";

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Public marketing page. NO auth required.
 * Renders live streams from /api/public/live-now with a "Join / Sign up to watch" CTA.
 */
export default function LiveNow() {
  const { user } = useAuth();
  const [streams, setStreams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [matchingBusy, setMatchingBusy] = useState(false);
  // Public visitors preview a specific side; authed viewers get their own.
  const [side, setSide] = useState(() => user?.side || readPublicSide());

  const fastestMatch = async () => {
    if (matchingBusy) return;
    setMatchingBusy(true);
    try {
      await api.post("/battles/random", { duration_seconds: 300 });
      toast.success("Random battle fired — waiting for opponent to accept.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start a random match.");
    } finally {
      setMatchingBusy(false);
    }
  };

  useEffect(() => {
    const load = async () => {
      try {
        const { data } = await axios.get(`${API_URL}/api/public/live-now`, { params: { side } });
        setStreams(data.streams || []);
      } catch (e) {
        setStreams([]);
      } finally {
        setLoading(false);
      }
    };
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, [side]);

  return (
    <div className="min-h-screen bg-black text-zinc-100" data-testid="live-now-root">
      <header className="border-b border-white/5 px-6 lg:px-10 py-5 flex items-center justify-between glass sticky top-0 z-10">
        <Link to="/" className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-full bg-rose-600 flex items-center justify-center text-white font-bold">P</div>
          <span className="font-display text-xl">My Panda X</span>
        </Link>
        <div className="flex items-center gap-3">
          {!user && (
            <PublicSidePill value={side} onChange={setSide} />
          )}
          <Link to="/login" className="text-xs uppercase tracking-widest text-zinc-400 hover:text-white">
            Log in
          </Link>
          <Link
            to="/register"
            data-testid="live-now-signup"
            className="btn-crimson rounded-none px-4 py-2 text-xs uppercase tracking-widest"
          >
            Sign up free
          </Link>
        </div>
      </header>

      <section className="px-6 lg:px-10 pt-14 pb-8 max-w-6xl mx-auto">
        <div className="text-xs uppercase tracking-[0.3em] text-rose-500 mb-4 flex items-center gap-2">
          <span className="w-1.5 h-1.5 rounded-full bg-rose-500 pulse-dot" /> Live right now
        </div>
        <h1 className="font-display text-5xl sm:text-6xl leading-[1.05] mb-4">
          Everything is <span className="text-rose-500">live</span> on My Panda X.
        </h1>
        <p className="text-zinc-400 text-base max-w-2xl leading-relaxed">
          Creators broadcasting to real communities. Send gifts, join battles, become a co-host,
          or start your own room. Free to sign up.
        </p>
        <div className="flex items-center gap-3 mt-6 flex-wrap">
          <Link
            to="/register"
            className="btn-crimson rounded-none px-5 py-3 text-sm uppercase tracking-widest"
            data-testid="live-now-cta-signup"
          >
            Sign up & watch →
          </Link>
          <Link
            to="/"
            className="text-sm text-zinc-400 hover:text-white"
          >
            Learn more
          </Link>
          {user && (
            <button
              type="button"
              onClick={fastestMatch}
              disabled={matchingBusy}
              data-testid="live-now-fastest-match"
              className="inline-flex items-center gap-2 bg-gradient-to-r from-rose-600 to-fuchsia-600 hover:brightness-110 disabled:opacity-50 text-white font-semibold rounded-full px-4 py-2 text-xs uppercase tracking-widest shadow-lg shadow-rose-500/20"
              title="Instantly challenge a random live streamer to a gift battle"
            >
              {matchingBusy ? <Loader2 size={12} className="animate-spin" /> : <Shuffle size={12} />}
              Fastest match
            </button>
          )}
        </div>
      </section>

      <section className="px-6 lg:px-10 pb-20 max-w-6xl mx-auto">
        {loading ? (
          <div className="text-sm text-zinc-500">Loading…</div>
        ) : streams.length === 0 ? (
          <div className="card-surface p-12 text-center">
            <Radio size={30} className="text-rose-500 mx-auto mb-3" />
            <h3 className="font-display text-2xl mb-2">Nobody's live right this second.</h3>
            <p className="text-sm text-zinc-500 mb-6">
              New streams start all the time.{" "}
              <Link to="/register" className="text-rose-500 hover:text-rose-400">Sign up</Link>
              {" "}to get notified when your favorites go live.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="live-now-grid">
            {streams.map((s) => (
              <Link
                key={s.id}
                to={`/register?ref=live_${s.id}`}
                data-testid={`live-now-card-${s.id}`}
                className="card-surface overflow-hidden hover-lift group"
              >
                <div className="aspect-[16/9] bg-zinc-900 relative overflow-hidden">
                  {s.thumbnail_url ? (
                    <img
                      src={s.thumbnail_url}
                      alt={s.title || "Untitled Live"}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-zinc-600">
                      <Radio size={30} />
                    </div>
                  )}
                  <div className="absolute top-2 left-2 flex items-center gap-1.5 bg-rose-600 text-white text-[10px] uppercase tracking-widest px-2 py-1">
                    <span className="w-1.5 h-1.5 rounded-full bg-white pulse-dot" /> Live
                  </div>
                  <div className="absolute top-2 right-2 flex items-center gap-1 bg-black/70 backdrop-blur text-white text-[10px] px-2 py-1">
                    <Users size={10} /> {s.viewer_count?.toLocaleString() || 0}
                  </div>
                </div>
                <div className="p-4 flex items-start gap-3">
                  <Avatar className="w-9 h-9 border border-white/10 shrink-0">
                    <AvatarImage src={s.host_avatar_url} />
                    <AvatarFallback className="bg-rose-600 text-xs">
                      {s.host_handle?.[0]?.toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium truncate">{s.title || "Untitled Live"}</div>
                    <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                      <div className="text-xs text-zinc-500 truncate">@{s.host_handle}</div>
                      {s.host_ambassador_status === "approved" && s.host_ambassador_tier && (
                        <span
                          data-testid={`live-now-ambassador-${s.host_handle}`}
                          className={`text-[9px] uppercase tracking-widest font-semibold rounded-full px-1.5 py-0.5 ${
                            s.host_ambassador_tier === "gold" ? "bg-gradient-to-r from-yellow-300 to-amber-500 text-black" :
                            s.host_ambassador_tier === "silver" ? "bg-gradient-to-r from-zinc-300 to-zinc-400 text-black" :
                            "bg-gradient-to-r from-amber-700 to-amber-500 text-black"
                          }`}
                          title={`Panda ${s.host_ambassador_tier} Ambassador`}
                        >
                          ★ Ambassador
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
        <div className="mt-10 text-center">
          <Button asChild className="btn-crimson rounded-none px-8 py-6 text-sm uppercase tracking-widest">
            <Link to="/register" data-testid="live-now-bottom-cta">Join My Panda X tonight →</Link>
          </Button>
        </div>
      </section>
    </div>
  );
}
